/**
 * Project Analysis API Service
 * Handles fetching and refreshing AI-powered project insights.
 */

import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

// Insight types
export interface KeyDecision {
  decision: string;
  source: string;
  status: 'proposed' | 'approved' | 'implemented';
  impact: 'high' | 'medium' | 'low';
}

export interface ProgressItem {
  item: string;
  status: 'completed' | 'in-progress' | 'blocked' | 'planned';
  owner?: string;
  source: string;
}

export interface OpenQuestion {
  question: string;
  context: string;
  source: string;
  priority: 'high' | 'medium' | 'low';
}

export interface Risk {
  risk: string;
  mitigation?: string;
  source: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
}

export interface AnalysisMetrics {
  totalDocuments: number;
  documentsByCategory: Record<string, number>;
  decisionsCount: number;
  openQuestionsCount: number;
  risksCount: number;
}

export interface ProjectInsights {
  summary: string;
  keyDecisions: KeyDecision[];
  progress: ProgressItem[];
  openQuestions: OpenQuestion[];
  risks: Risk[];
  metrics: AnalysisMetrics;
}

export interface ProjectAnalysis {
  analyzedAt: string;
  isStale: boolean;
  documentCount: number;
  insights: ProjectInsights;
}

export interface AnalysisNotFoundResponse {
  message: string;
  documentCount: number;
}

class ProjectAnalysisApiService extends BaseApiService {
  constructor() {
    super(API_URL, 'ProjectAnalysisApi');
  }

  /**
   * Get cached project analysis.
   * Returns null if no analysis exists yet.
   */
  async getAnalysis(): Promise<ProjectAnalysis | null> {
    if (!this.baseUrl) {
      this.log.error('API URL not configured');
      throw new Error('Analysis API not configured');
    }

    this.log.debug('Fetching project analysis');
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/analysis/project`, {
      method: 'GET',
      headers,
    });

    if (response.status === 404) {
      const data = await response.json() as AnalysisNotFoundResponse;
      this.log.info('No analysis found', { documentCount: data.documentCount });
      return null;
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get analysis', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get analysis');
    }

    const analysis = await response.json() as ProjectAnalysis;
    this.log.info('Analysis retrieved', {
      analyzedAt: analysis.analyzedAt,
      isStale: analysis.isStale,
      documentCount: analysis.documentCount,
    });

    return analysis;
  }

  /**
   * Force refresh analysis by re-analyzing all documents.
   */
  async refreshAnalysis(): Promise<ProjectAnalysis> {
    if (!this.baseUrl) {
      this.log.error('API URL not configured');
      throw new Error('Analysis API not configured');
    }

    this.log.info('Refreshing project analysis');
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/analysis/project/refresh`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to refresh analysis', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to refresh analysis');
    }

    const analysis = await response.json() as ProjectAnalysis;
    this.log.info('Analysis refreshed', {
      analyzedAt: analysis.analyzedAt,
      documentCount: analysis.documentCount,
    });

    return analysis;
  }
}

export const projectAnalysisApi = new ProjectAnalysisApiService();
export default projectAnalysisApi;
